/**
 * Two-tab switcher for Bank rules vs Integration rules.
 *
 * Horizontal tab row with an underline indicator for the active
 * tab and a count badge on each tab. Follows the Novala classical
 * aesthetic: ink underline, no color fill, muted inactive labels.
 *
 * Mobile: compact labels ("Bank" and "Integration") with smaller
 * padding so both tabs still fit without wrapping.
 *
 * The parent (RulesPage) owns the active-tab state because the
 * tab choice drives the data fetched by useRules and the
 * customization loaded by useRulesCustomization. This component
 * is a pure controlled input: value in, change event out.
 */

import type { CSSProperties } from "react";
import type { RuleTab } from "../rulesTypes";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
};

interface RulesTabsProps {
  activeTab: RuleTab;
  onTabChange: (tab: RuleTab) => void;
  bankCount: number;
  integrationCount: number;
}

export function RulesTabs({
  activeTab,
  onTabChange,
  bankCount,
  integrationCount,
}: RulesTabsProps) {
  const { isMobile } = useBreakpoint();

  return (
    <div
      style={isMobile ? rowStyleMobile : rowStyle}
      role="tablist"
      aria-label="Rules type"
    >
      <TabButton
        isActive={activeTab === "bank"}
        onClick={() => onTabChange("bank")}
        label={isMobile ? "Bank" : "Bank rules"}
        count={bankCount}
        isMobile={isMobile}
      />
      <TabButton
        isActive={activeTab === "integration"}
        onClick={() => onTabChange("integration")}
        label={isMobile ? "Integration" : "Integration rules"}
        count={integrationCount}
        isMobile={isMobile}
      />
    </div>
  );
}

interface TabButtonProps {
  isActive: boolean;
  onClick: () => void;
  label: string;
  count: number;
  isMobile: boolean;
}

function TabButton({ isActive, onClick, label, count, isMobile }: TabButtonProps) {
  const style: CSSProperties = isMobile
    ? { ...tabStyleBase, ...tabStyleMobile, ...(isActive ? tabStyleActive : {}) }
    : { ...tabStyleBase, ...(isActive ? tabStyleActive : {}) };

  const countStyle: CSSProperties = {
    ...countStyleBase,
    ...(isActive ? countStyleActive : {}),
  };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      style={style}
    >
      {label}
      <span style={countStyle}>{count}</span>
    </button>
  );
}

const rowStyle: CSSProperties = {
  padding: "14px 32px 0",
  borderBottom: `1px solid ${PALETTE.line}`,
  display: "flex",
  gap: 2,
  background: "#ffffff",
  width: "100%",
};

const rowStyleMobile: CSSProperties = {
  ...rowStyle,
  padding: "10px 16px 0",
};

const tabStyleBase: CSSProperties = {
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 600,
  color: PALETTE.ink3,
  background: "transparent",
  border: 0,
  borderBottom: "2px solid transparent",
  cursor: "pointer",
  marginBottom: -1,
  fontFamily: "inherit",
  transition: "color 0.12s, border-color 0.12s",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const tabStyleMobile: CSSProperties = {
  padding: "8px 12px",
  fontSize: 12,
};

const tabStyleActive: CSSProperties = {
  color: PALETTE.ink,
  borderBottomColor: PALETTE.ink,
};

const countStyleBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 20,
  height: 18,
  padding: "0 6px",
  fontSize: 11,
  fontWeight: 600,
  background: PALETTE.bgFade,
  color: PALETTE.ink3,
  borderRadius: 9,
};

const countStyleActive: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
};